import React, { useState, useMemo, useCallback } from "react";
import { observer } from "mobx-react-lite";

import useThemeService from "../../../services/theme-service/ThemeService";
import {
  UploadSewerObjects,
  UploadSewerObjectsToProjectAssignedTo,
} from "../../../services/authorization/Permissions";
import { permissionsService } from "../../../services/authorization/UserPermissionsService";
import { translationService } from "../../../services/translation/TranslationService";
import { FileExtended } from "../../../services/upload/FileUploadTracker";
import { InspectionFilesStorage } from "../../../services/inspection/InspectionFilesStorage";
import { CurrentInspectionStorage } from "../../../services/inspection/CurrentInspectionStorage";
import Button from "../../../components/button/Button";
import { Upload as UploadSvg } from "../../../components/svg/svgImageList";
import { Dialog } from "../../../components/dialog/Dialog";
import DropFiles from "../../../components/upload-drop-files/DropFiles";
import UploadTable from "../../../components/upload-drop-files/UploadTable";

export default observer(({ inspectionId, inspectionFilesStorage, currentInspectionStorage }: {
  inspectionId: number,
  inspectionFilesStorage: InspectionFilesStorage
  currentInspectionStorage: CurrentInspectionStorage
}) => {
  const { themeClass } = useThemeService();

  const [filesToUpload, setFilesToUpload] = useState<Array<FileExtended>>([]);

  const videoAlreadyUploaded = useMemo(() =>
    currentInspectionStorage.inspectionHasVideo
    && filesToUpload.some(f => f.isVideoFileType),
    [currentInspectionStorage.inspectionHasVideo, filesToUpload]);

  const multipleVideosToUpload = useMemo(() =>
    filesToUpload.filter(f => f.isVideoFileType).length > 1,
    [filesToUpload]);

  const isUploadDisabled = useMemo(() =>
    inspectionFilesStorage.uploadService.isUploadOngoing
    || (!permissionsService.userHasPermissionTo(UploadSewerObjects)
      && !permissionsService.userHasPermissionTo(UploadSewerObjectsToProjectAssignedTo)),
    [inspectionFilesStorage.uploadService.isUploadOngoing]);

  const isUploadButtonDisabled = useMemo(() =>
    isUploadDisabled
    || filesToUpload.length === 0
    || videoAlreadyUploaded
    || multipleVideosToUpload,
    [isUploadDisabled, filesToUpload.length, videoAlreadyUploaded, multipleVideosToUpload]);

  const [isUploadModalVisible, setUploadModalVisible] = useState(false);
  const handleDialogClose = useCallback(() => {
    setUploadModalVisible(false);
    setFilesToUpload([]);
  }, []);

  const addFilesForUpload = useCallback((droppedFiles: Array<FileExtended>) =>
    setFilesToUpload([...filesToUpload, ...droppedFiles.filter((newFile) =>
      !filesToUpload.some((oldFile) => oldFile.file.name === newFile.file.name && oldFile.path === newFile.path)
    )]),
    [filesToUpload]);

  const removeFile = useCallback((fileName: string) =>
    setFilesToUpload(filesToUpload.filter(f => f.name !== fileName)),
    [filesToUpload]);

  const handleUploadClick = useCallback(async () => {
    handleDialogClose();
    inspectionFilesStorage.uploadService.uploadMediaFiles(filesToUpload, inspectionId);
  }, [filesToUpload, handleDialogClose, inspectionFilesStorage.uploadService, inspectionId]);

  return (
    <>
      <Button
        disabled={isUploadDisabled}
        id="button-upload-file"
        icon={{
          svg: UploadSvg,
          textKey: "general.upload.files",
          textClass: "actions-header-button-label",
          className: themeClass(["icon-upload"]),
        }}
        onClick={() => setUploadModalVisible(true)}
      />
      <Dialog
        sidePosition
        isVisible={isUploadModalVisible}
        onHide={handleDialogClose}
        titleKey="general.upload.files"
        buttonOk={{
          textKey: "general.upload",
          action: handleUploadClick,
          disabled: isUploadButtonDisabled,
        }}
        buttonCancel={{
          textKey: "general.cancel",
          action: handleDialogClose,
        }}
      >
        <div className="upload-modal-content">
          <div className="upload-details">
            {translationService.translate("UploadFileToInspection.upload-modal-details")}
          </div>
          <DropFiles onDroppedFiles={addFilesForUpload} />
          <div className="upload-details">
            {translationService.translate("UploadFileToInspection.upload-modal-summary",
              { filesNumber: filesToUpload.length })}
          </div>
          {(videoAlreadyUploaded || multipleVideosToUpload) && (
            <div className="error-upload-message">
              {translationService.translate("UploadFileToInspection.upload-modal.video-already-uploaded-message")}
            </div>
          )}
          {filesToUpload.length !== 0 && (
            <UploadTable files={filesToUpload} onRemoveFile={removeFile} />
          )}
        </div>
      </Dialog>
    </>
  );
});
